﻿import { Directive } from "@app/components/directive";
import { ITabScope } from "@app/components/kb-tabs/kb-tab.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";

export interface ITabsScope extends ng.IScope {
    /** 2 way binding that holds the value of the currently selected tab */
    selectedTab: string;
    /** event fires when the selected tab has changed.  Has signature of: (oldTab: string, newTab: string) => void;  */
    tabChanged: any;  
    /** aligns with eTabWidth enum */
    tabWidth: string;
    /** sets the width of tab header if tabWidth is set to 'custom' */
    customTabWidth: number;
    /** the actual selected kbTab scope */
    _selectedTabScope: ITabScope;
    _tabs: ITabScope[];
    _select: (tab: ITabScope) => void;
    _lineLeft: number;
    _lineWidth: number;
    _headerClick: (e: JQueryEventObject, tab: ITabScope) => void;
    _paged: boolean;
    _scrollNext: () => void;
    _scrollPrev: () => void;
    _pageNextVisible: boolean;
    _pagePrevVisible: boolean;
}

export interface ITabsController {
    scope: ITabsScope;
    registerTab: (tab: ITabScope) => void;
    tabVisibleChanged: (tab: ITabScope) => void;
}

/**
 * kbTabs
 *
 * kbTabs is utilized by having a parent kb-tabs element, and inside of it a kb-tab-pane (which are the headers)
 * and many kb-tab directives.  You can place the kb-tabs-pane and the kb-tabs wherever you like, as long as they
 * are inside the kb-tabs directive.  This gives a flexible approach which we can use in any situation.
 *    
 * <kb-tabs>
 *     <kb-tab-pane></kb-tab-pane>
 *     <kb-tab name="tab1" label="Contacts"></kb-tab>
 *     <kb-tab name="tab2" label="Email"></kb-tab>
 * </kb-tabs>
 *
 * you can remotely control the selected tab by using a two-way binding with selectedTab scope property.  This
 * property is looking for the name of the tab.  
 *
 */

@NgComponent({
    token: Token.KbTabs,
    dependencies: [
        Token.$Timeout,
        Token.$Window
    ]
})
export class KbTabs extends Directive {
    constructor($timeout: ng.ITimeoutService, $window: ng.IWindowService) {

        super();

        this.replace = true;
        this.template = Dirs.component("kb-tabs");
        this.restrict = "AE";
        this.scope = {
            selectedTab: "=?",
            tabChanged: "&",
            tabWidth: "=?",
            customTabWidth: "=?"
        };
        this.transclude = true;

        // add scope defaults to the compile function
        this.compile = (element, atts: any) => {

        };

        this.controller = [
            "$scope",
            "$element",
            "$attrs",
            "$transclude",
            function(
                $scope: ITabsScope,
                $element: ng.IAugmentedJQuery,
                $attrs: ng.IAttributes,
                $transclude: ng.ITranscludeFunction) {

                // set the scope of the tree on it's controller so children can access it
                this.scope = $scope;
                let locked = false; //during tab animations we lock the user from switching tabs
                let firstRun = true;
                let scrollLeft: number = 0;
                $scope._tabs = [];
                let $container = $element.find(".kb-tabs__tab-container");
                let $headers = $element.find(".kb-tabs__headers");
                let $pane = $element.find(".kb-tabs__pane");
                let $headersContainer = $element.find(".kb-tabs__headers-container");
                let selectionLineEl = $element.find(".kb-tabs__selection-line")[0];

                $scope._select = tab => {
                    $scope.selectedTab = tab.value;
                };
                $scope._headerClick = (e, tab) => {
                    if (!locked) {
                        $scope._select(tab);
                    }    
                }

                let setSelectedTabScope = (tab: ITabScope) => {
                    let oldTab = $scope._selectedTabScope;
                    let newTab = tab;

                    $scope._selectedTabScope = tab;

                    if (oldTab !== newTab) {
                        firstRun ? $timeout(() => positionSelectionLine(tab), 0) : positionSelectionLine(tab);
                        scrollSelectedIntoView();
                        animateContent(oldTab, newTab);
                        if (!firstRun && $scope.tabChanged) { //for the first run, we are just selecting the first visible tab.  This should not count as a tabChanged event
                            firstRun = false;
                            $scope.tabChanged({ oldTab: oldTab ? oldTab.value : null, newTab: newTab ? newTab.value : null });
                        }
                        firstRun = false;
                        
                    }    
                };

                let positionSelectionLine = (tab: ITabScope) => {
                    let headerEl = $element.find(`#${tab.$id}`)[0];
                    
                    if (headerEl) {
                        selectionLineEl.style.left = headerEl.offsetLeft + "px";
                        selectionLineEl.style.width = headerEl.offsetWidth + "px";
                    }                   
                };

                let animateContent = (oldTab: ITabScope, newTab: ITabScope) => {
                    locked = true;
                    let oldTabIndex = $scope._tabs.indexOf(oldTab);
                    let newTabIndex = $scope._tabs.indexOf(newTab);
                    let $oldTabEl = oldTab && oldTab._getElement();
                    let $newTabEl = newTab && newTab._getElement();

                    let oldTabClass: string;
                    let newTabClass: string;
                    newTab && $newTabEl.css("transition", "none");
                    if (newTabIndex > oldTabIndex) {
                        //oldTab && $oldTabEl.css("transform", "translate3d(-100%,0,0)");
                        newTab && $newTabEl.css("transform", "translate3d(100%,0,0)");                        
                    } else {
                        newTab && $newTabEl.css("transform", "translate3d(-100%,0,0)");
                    }

                    if (newTab) newTab.selected = true;        
                    

                    setTimeout(() => {
                        //animate the height also.     
                        let currentHeight = oldTab && $oldTabEl.outerHeight();
                        newTab && $newTabEl.css("position", "relative");
                        // oldTab && $oldTabEl.css("position", "absolute");
                        // $container.height("auto");
                        let newHeight = newTab && $newTabEl.outerHeight(); // $container[0].offsetHeight;
                        newTab && $newTabEl.css("position", "absolute");
                        // oldTab && $oldTabEl.css("position", "relative");
                        $container.height(currentHeight);

                        if (newTabIndex > oldTabIndex) {
                            oldTab && $oldTabEl.css("transform", "translate3d(-100%,0,0)");
                        } else {
                            oldTab && $oldTabEl.css("transform", "translate3d(100%,0,0)");
                        }
                        newTab && $newTabEl.css("transition", "");
                        newTab && $newTabEl.css("transform", "translate3d(0,0,0)");

                        $container.height(newHeight);

                        // $timeout(() => {
                            
                        // }, 0);

                        $timeout(() => {
                            if (oldTab) oldTab.selected = false;
                            //on the first run the new tab element might not have existed until now, so we refresh the reference
                            $newTabEl = newTab && $element.find(`#tab-${newTab.$id}`);
                            newTab && $newTabEl.css("position", "relative");
                            oldTab && $oldTabEl.css("position", "absolute");

                            $container.height("auto");
                            locked = false;
                        }, 300);
                    }, 0);
                };

                
                
                // all child nodes register themselves with the tree to handle selection and accordion functionality
                this.registerTab = (tab: ITabScope) => {
                    $scope._tabs.push(tab);
                    tab.$on("$destroy", event => {
                        $scope._tabs.remove(tab);

                        let selectedTab = $scope._tabs.find(t => t.selected);
                        if (selectedTab) {
                            $scope._select(selectedTab);
                            $timeout(() => positionSelectionLine(selectedTab), 0);
                        }

                    });
                    updateSelectedTabScope();
                };

                this.tabVisibleChanged = (tab: ITabScope) => {
                    //if the selected tab isn't visible then select the first visible one
                    if (tab.selected && !tab.visible) {
                        $scope.selectedTab = null;
                        $timeout(() => {
                            updateSelectedTabScope();
                        });
                    } else {
                        $timeout(() => {
                            if ($scope._selectedTabScope) positionSelectionLine($scope._selectedTabScope);
                        });
                    }
                };

                let updateSelectedTabScope = () => {
                    if (!$scope.selectedTab && $scope._tabs.some(t => t.visible)) {
                        $scope.selectedTab = $scope._tabs.find(t => t.visible).value;
                    }
                    for (let tab of $scope._tabs) {
                        if (tab.value === $scope.selectedTab) {
                            setSelectedTabScope(tab);
                            break;
                        }
                    }                    
                };
                // if the consumer changes the selected item, we update the selected node
                $scope.$watch("selectedTab", (newVal, oldVal) => {
                    updateSelectedTabScope();
                });
                $scope.$watch("tabWidth", () => {
                    if ($scope._selectedTabScope) $timeout(() => positionSelectionLine($scope._selectedTabScope), 300);
                });
                $scope.$watch("customTabWidth", () => {
                    if ($scope._selectedTabScope) $timeout(() => positionSelectionLine($scope._selectedTabScope), 300);
                });

                let updatePagination = () => {
                    let containerWidth = $headersContainer.width();
                    let headersWidth = $headers.width();
                    let paneWidth = $pane.width();
                    $scope._paged = (headersWidth > paneWidth);
                    $scope._pageNextVisible = (headersWidth > containerWidth + scrollLeft);
                    $scope._pagePrevVisible = (scrollLeft > 0);
                };

                let scrollSelectedIntoView = () => {
                    let headerEl = $element.find(`#${$scope._selectedTabScope.$id}`)[0];
                    if (headerEl) {
                        let elemLeft = headerEl.offsetLeft;
                        let elemRight = elemLeft + headerEl.offsetWidth;
                        let containerWidth = $headersContainer[0].clientWidth;

                        if (elemLeft - scrollLeft < 0) {
                            setScroll(elemLeft);
                        } else if (elemRight > scrollLeft + containerWidth) {
                            setScroll(elemRight - containerWidth);
                        }
                    }
                };

                $scope._scrollNext = () => {
                    setScroll(scrollLeft + $headersContainer.width());
                };

                $scope._scrollPrev = () => {
                    setScroll(scrollLeft - $headersContainer.width());
                };

                let setScroll = (val: number) => {
                    scrollLeft = fixScroll(val);
                    $headers.css("transform", `translateX(${-scrollLeft}px)`);
                    updatePagination();
                };

                let scroll = (e: JQueryEventObject) => {
                    if ($scope._paged) {
                        e.preventDefault();
                        setScroll(scrollLeft - (e as any).wheelDelta);
                        $scope.$digest();
                    }
                };

                let fixScroll = (value: number): number => {
                    if (!$scope._paged) return 0;
                    if (value < 0) return 0;
                    let headersWidth = $headers.width();
                    let containerWidth = $headersContainer.width();

                    if (value > headersWidth - containerWidth) return headersWidth - containerWidth;  
                    return value;
                };

                let windowResize = (e: JQueryEventObject) => {
                    updatePagination();
                    setScroll(scrollLeft);
                    $scope.$digest();
                };

                $headersContainer[0].addEventListener("mousewheel", scroll);
                $($window).on("resize", windowResize);


                $timeout(() => {
                    updatePagination();
                }, 0);

                $scope.$on("$destroy", () => {
                    if ($headers && $headers.length) $headers[0].removeEventListener("mousewheel", scroll);
                    $($window).off("resize", windowResize);
                });
            }];
    }
}
